import classNames from "classnames";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { ReactNode, useState } from "react";
import {
  StateRegistrationName,
  StateRegistrationNameType,
  StateRegistrationsRes,
} from "store/apis/StateRegistrations";
import {
  RegistrationStatus,
  RegistrationStatusName,
} from "store/apis/registrations";
import {
  DONE_STATUSES,
  INKLE_HANDLES,
  REGISTRATION_CARD_LABELS,
  REGISTRATION_DESCRIPTIONS,
  REGISTRATION_TYPES,
} from "./constants";
import { Info } from "components/icons/Info";
import { FilingsSparkle } from "components/icons/FilingsSparkle";
import TicketResolved from "static/images/TicketResolved.svg";
import { ROW_WHAT_INKLE_DOES } from "./constants";

// ─── ServiceCard ──────────────────────────────────────────────────────────────

type BundledSubItem = {
  type: StateRegistrationNameType;
  status: RegistrationStatus;
  statusText: string;
  cartStatus: string | null;
};

const ServiceHint = ({ children }: { children: ReactNode }) => (
  <span className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-blue t-italic">
    <Info size="12" />
    {children}
  </span>
);

export const ServiceCard = ({
  type,
  status,
  cartStatus,
  checked,
  onChange,
  bundledItem,
  onBundledToggle,
  stateName,
}: {
  type: StateRegistrationNameType;
  status: RegistrationStatus;
  statusText: string;
  cartStatus: string | null;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  bundledItem?: BundledSubItem;
  onBundledToggle?: () => void;
  stateName: string;
}) => {
  const [hasOwnRa, setHasOwnRa] = useState(false);

  const isDone =
    DONE_STATUSES.includes(status) ||
    status === RegistrationStatusName.NOT_APPLICABLE;

  const bundledIsDone = bundledItem
    ? DONE_STATUSES.includes(bundledItem.status) ||
      bundledItem.status === RegistrationStatusName.NOT_APPLICABLE
    : false;

  const isPayroll = type === StateRegistrationName.PAY_ROLL;

  return (
    <div
      className={classNames(
        "t-w-full t-flex t-flex-col t-rounded-lg t-border t-border-solid t-bg-white t-overflow-hidden",
        {
          "t-border-neutral-10": !isDone,
          "t-border-neutral-10 t-opacity-50": isDone,
        }
      )}
    >
      <div
        role="button"
        tabIndex={isDone ? -1 : 0}
        onClick={() => !isDone && onChange(!checked)}
        onKeyDown={(e) => {
          if (!isDone && (e.key === "Enter" || e.key === " "))
            onChange(!checked);
        }}
        className={classNames("t-flex t-items-start t-gap-3 t-p-5 sm:t-p-4", {
          "t-cursor-pointer": !isDone,
          "t-cursor-not-allowed": isDone,
        })}
      >
        <div className="t-mt-0.5 t-shrink-0">
          <Checkbox
            checked={checked}
            disabled={isDone}
            onChange={() => {}}
            aria-label={REGISTRATION_CARD_LABELS[type]}
          />
        </div>
        <div className="t-flex t-flex-col t-gap-1 t-flex-1">
          <span className="t-flex t-items-center t-gap-1.5 t-text-subtext t-text-text-100">
            {REGISTRATION_CARD_LABELS[type]}
          </span>
          <span className="t-text-body-sm t-text-text-30">
            {REGISTRATION_DESCRIPTIONS[type](stateName)}
          </span>
          {bundledItem && !bundledIsDone && (
            <ServiceHint>RA is included in this service</ServiceHint>
          )}
          {isPayroll && (
            <ServiceHint>
              Only needed if you have employee in {stateName}
            </ServiceHint>
          )}
          {cartStatus && (
            <span className="t-text-body-sm t-text-text-30 t-italic">
              Already in cart
            </span>
          )}
        </div>
      </div>

      {!isDone && bundledItem && !bundledIsDone && (
        <label
          className="t-flex t-items-center t-gap-2 t-px-5 t-py-3 sm:t-px-4 t-text-body-sm t-text-text-60 t-cursor-pointer t-border-0 t-border-t t-border-dashed t-border-neutral-10"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={hasOwnRa}
            onChange={() => {
              setHasOwnRa((prev) => !prev);
              onBundledToggle?.();
            }}
          />
          I already have an RA in {stateName}
        </label>
      )}
    </div>
  );
};

// ─── BannerStep1 ──────────────────────────────────────────────────────────────

export const BannerStep1 = ({
  stateData,
  selected,
  onToggle,
  stateName,
}: {
  stateData: StateRegistrationsRes;
  selected: StateRegistrationNameType[];
  onToggle: (type: StateRegistrationNameType) => void;
  stateName: string;
}) => {
  const raReg = stateData[StateRegistrationName.REGISTERED_AGENT];

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {REGISTRATION_TYPES.filter(
        (type) =>
          type !== StateRegistrationName.REGISTERED_AGENT &&
          !(
            stateData.is_home_state &&
            type === StateRegistrationName.SECRETARY_OF_STATE
          )
      ).map((type) => {
        const reg = stateData[type];
        const isDone =
          DONE_STATUSES.includes(reg.status) ||
          reg.status === RegistrationStatusName.NOT_APPLICABLE;

        const bundledItem =
          type === StateRegistrationName.SECRETARY_OF_STATE
            ? {
                type: StateRegistrationName.REGISTERED_AGENT,
                status: raReg.status,
                statusText: raReg.status_text,
                cartStatus: raReg.cart_status,
              }
            : undefined;

        return (
          <ServiceCard
            key={type}
            type={type}
            status={reg.status}
            statusText={reg.status_text}
            cartStatus={reg.cart_status}
            checked={selected.includes(type)}
            disabled={isDone}
            onChange={() => onToggle(type)}
            bundledItem={bundledItem}
            onBundledToggle={
              bundledItem ? () => onToggle(bundledItem.type) : undefined
            }
            stateName={stateName}
          />
        );
      })}
    </div>
  );
};

// ─── RowStep1 ─────────────────────────────────────────────────────────────────

export const RowStep1 = ({ type }: { type: StateRegistrationNameType }) => {
  const meta = ROW_WHAT_INKLE_DOES[type];
  const bullets = INKLE_HANDLES[type];

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <p className="t-text-body t-text-text-60 t-m-0">{meta.subtitle}</p>
      <div className="t-flex t-flex-col t-gap-3">
        <span className="t-flex t-items-center t-gap-1.5 t-text-body-sm  t-tracking-wider t-text-orange-70">
          <FilingsSparkle />
          <span className="t-text-neutral-40 t-text-body-sm">
            WHAT INKLE HANDLES
          </span>
        </span>
        <div className="t-flex t-flex-col t-gap-2.5">
          {bullets.map((bullet) => (
            <div key={bullet} className="t-flex t-items-center t-gap-2.5">
              <img
                src={TicketResolved}
                alt="TicketResolved"
                className="t-size-4"
              />
              <span className="t-text-body t-text-text-60">{bullet}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
